import React from 'react'

import { makeStyles } from '@material-ui/core/styles'
import {
  Box,
  FormControl,
  Grid,
  RadioGroup,
  Theme,
  Typography,
} from '@material-ui/core'
import SpeciesSelectionBox from './components/SpeciesSelectionBox'
import { FormattedMessage } from 'react-intl'
import { Skeleton } from '@material-ui/lab'
import { useFormikContext } from 'formik'
import { useRouter } from 'next/router'
import { MilliplexFormValues } from '../../Milliplex'
import ValidationErrors from '@src/routes/Configurators/validations/ValidationErrors'
import { checkForDefaultAssay } from '@src/routes/Configurators/products/Milliplex/utils'
import { AssayTypeItem } from '@src/types/graphql-types'

const useStyles = makeStyles((theme: Theme) => {
  return {
    part1HeaderStyle: {
      marginTop: theme.spacing(6),
    },
    speciesHeaderStyle: {
      margin: theme.spacing(6, 0, 4, 0),
      fontSize: '16px',
    },
    speciesSkeleton: {
      minWidth: '100%',
      height: theme.typography.pxToRem(110),
      display: 'block',
      border: 'solid 1px',
      borderColor: theme.palette.grey[500],
      borderRadius: '5px',
      marginLeft: 0,
      marginRight: 0,
    },
    skeletonCardContainer: {
      height: theme.typography.pxToRem(85),
    },
    skeletonContainer: {
      marginTop: '-32px',
      marginBottom: theme.spacing(3.5),
    },
    container: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '16px',
    },
    item: {
      flexBasis: '100%',
      [theme.breakpoints.up('sm')]: {
        flexBasis: '100%',
        maxWidth: '50%',
      },
      [theme.breakpoints.up('md')]: {
        flexBasis: 'calc(50% - 16px)',
      },
      [theme.breakpoints.up('lg')]: {
        flexBasis: 'calc(33% - 8px)',
      },
      [theme.breakpoints.up('xl')]: {
        flexBasis: 'calc(25% - 16px)',
      },
    },
  }
})

export interface SpeciesProps {
  listSpecies: string[]
  loading: boolean
  setDisplayPageError: React.Dispatch<React.SetStateAction<boolean>>
  assayTypeList: AssayTypeItem[]
}

const Species: React.FC<SpeciesProps> = ({
  listSpecies,
  loading,
  setDisplayPageError,
  assayTypeList,
}) => {
  const classes = useStyles()
  const router = useRouter()

  const formik = useFormikContext<MilliplexFormValues>()
  const onSpeciesChange = (e) => {
    const assayDefault = checkForDefaultAssay({
      assayTypeList: assayTypeList,
      species: e.target.value,
      researchAreas: [],
    })

    formik.setValues({
      ...formik.values,
      species: e.target.value,
      researchAreas: [],
      assay: assayDefault,
      plateType: '',
      instrumentType: [],
      analytes: [],
    })
    formik.setFieldTouched('analytes', false)
    setDisplayPageError(false)
    const assayQuery = assayDefault !== '' ? { assay: assayDefault } : {}
    router.replace(
      {
        pathname: router.pathname,
        query: {
          activeLink: '',
          country: router.query?.country,
          language: router.query?.language,
          species: e.target.value,
          ...assayQuery,
        },
      },
      undefined,
      { shallow: true }
    )
  }
  return (
    <>
      <Typography variant="h3" className={classes.part1HeaderStyle}>
        <FormattedMessage
          id="PART_1_SEARCH_CRITERIA"
          defaultMessage="Part 1. Search Criteria"
        />
      </Typography>
      <Typography variant="subtitle2" className={classes.speciesHeaderStyle}>
        <FormattedMessage
          id="SELECT_A_SPECIES"
          defaultMessage="Select a Species"
        />
      </Typography>
      <FormControl component="fieldset" fullWidth>
        <RadioGroup
          row
          aria-label="species"
          name="species"
          value={formik.values.species}
          onChange={onSpeciesChange}
        >
          {loading && !listSpecies.length && (
            <Grid container spacing={4} className={classes.skeletonContainer}>
              {Array(11)
                .fill(undefined)
                .map((_, index) => (
                  <Grid
                    key={index}
                    xs={12}
                    md={4}
                    item
                    className={classes.skeletonCardContainer}
                  >
                    <Skeleton
                      animation="wave"
                      className={classes.speciesSkeleton}
                    />
                  </Grid>
                ))}
            </Grid>
          )}
          {!loading && listSpecies.length && (
            <Box className={classes.container}>
              {listSpecies.map((species, index) => (
                <Box key={index} className={classes.item}>
                  <SpeciesSelectionBox
                    name={species}
                    checked={species === formik.values.species}
                  />
                </Box>
              ))}
            </Box>
          )}
        </RadioGroup>
      </FormControl>
      <ValidationErrors
        errors={
          formik.touched.species &&
          formik.errors.species &&
          !formik.values.species
            ? [formik.errors.species]
            : []
        }
        configuratorsRedesign
      />
    </>
  )
}

export default Species
